/**
 * One circle recommendation in the nav discovery menu (Figma 4327-59773):
 * name + Join CTA on one row, a members count line, then the description.
 *
 * A separate component so it can `React.memo` — the parent re-renders on every
 * dismissal and join — and so the copy lookup stays out of the parent's
 * `.map()`.
 *
 * Deferred-i18n (unlaunched Circles beta): copy is inlined English, matching
 * `ShareToCircle`'s COPY object, `Tarot/*` and `Chat/ChatMessageCard/*`.
 * Smartling bills on prod merge, so keys for a gated feature that may not ship
 * are a real cost — localise as part of launching the gate.
 */
import React, { useCallback } from 'react'
import Button, { TYPES } from '../../../Button/index'
import Users from '../../../Icon/icons/Users/index'
import type { NavMenuOption } from './navMenuCategoryCopy'

const COPY = {
  join: 'Join',
  joined: 'Joined',
  members: (count: number, capacity: number) => `${count} / ${capacity} members`,
}

export interface CirclesNavMenuCardProps {
  option: NavMenuOption
  joined: boolean
  /** A join (any card's) is in flight — disable the CTA so joins are single-flight. */
  pending?: boolean
  /** Joining is by rainbow category, not feed id — that is assignToCircle's argument. */
  onJoin: (rainbowCategoryKey: number) => void
  /**
   * Id of the panel's "you can leave anytime" line, described onto the CTA so
   * the promise reaches a reader who tabs straight to Join (the panel's reading
   * order can't carry it). Mirrors OnboardingCirclePicker's `describedBy`.
   */
  describedBy?: string
}

const CirclesNavMenuCardBase: React.FC<CirclesNavMenuCardProps> = ({
  option, joined, pending = false, onJoin, describedBy,
}) => {
  const handleJoin = useCallback(
    () => {
      // Guard instead of native-disabling on `pending`: `disabled` on the
      // focused CTA would drop keyboard focus to <body> inside the open
      // dialog. (The hook's joinPendingRef is the real single-flight gate —
      // this just keeps the click inert.)
      if (pending || joined) return
      onJoin(option.rainbowCategoryKey)
    },
    [onJoin, option.rainbowCategoryKey, pending, joined],
  )

  return (
    <li className="circles-nav-menu__card">
      <div className="circles-nav-menu__card-head">
        <span className="circles-nav-menu__card-name">{option.name}</span>
        <Button
          type={TYPES.SECONDARY}
          className={`circles-nav-menu__card-cta${pending && !joined ? ' circles-nav-menu__card-cta--pending' : ''}`}
          onClick={handleJoin}
          disabled={joined}
          aria-disabled={pending && !joined ? true : undefined}
          aria-label={`${joined ? COPY.joined : COPY.join} ${option.name}`}
          aria-describedby={describedBy}
        >
          {joined ? COPY.joined : COPY.join}
        </Button>
      </div>
      <div className="circles-nav-menu__card-meta">
        <Users size="16px" color="currentColor" />
        <span>{COPY.members(option.memberCount, option.capacity)}</span>
      </div>
      <p className="circles-nav-menu__card-desc">{option.description}</p>
    </li>
  )
}

export const CirclesNavMenuCard = React.memo(CirclesNavMenuCardBase)

export default CirclesNavMenuCard
